// SPDX-License-Identifier: AGPL-3.0-or-later

import {IMAGE_MAX_SIZE_LABEL} from '@app/features/app/config/I18nDisplayConstants';
import {openFilePicker} from '@app/features/messaging/utils/FilePickerUtils';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {Button} from '@app/features/ui/button/Button';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Input} from '@app/features/ui/components/form/Input';
import {Select} from '@app/features/ui/components/form/Select';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import * as DateUtils from '@app/features/user/utils/dateFormatting';
import styles from '@app/features/webhook/components/WebhookListItem.module.css';
import type {Webhook} from '@app/features/webhook/models/Webhook';
import {Trans, useLingui} from '@lingui/react/macro';
import {CaretDownIcon, CopySimpleIcon, TrashSimpleIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';

interface ChannelOption {
	id: string;
	label: string;
}
interface WebhookListItemProps {
	webhook: Webhook;
	channelName?: string | null;
	onDelete: (webhook: Webhook) => Promise<void>;
	onUpdate: (webhookId: string, updates: {name?: string; avatar?: string | null; channelId?: string}) => void;
	availableChannels?: Array<ChannelOption>;
	defaultExpanded?: boolean;
	isExpanded?: boolean;
	onExpandedChange?: (expanded: boolean) => void;
	formVersion?: number;
}

const logger = new Logger('WebhookListItem');
export const WebhookListItem: React.FC<WebhookListItemProps> = observer(
	({
		webhook,
		channelName,
		onDelete,
		onUpdate,
		availableChannels,
		defaultExpanded = false,
		isExpanded,
		onExpandedChange,
		formVersion,
	}) => {
		const {t, i18n} = useLingui();
		const [localExpanded, setLocalExpanded] = useState(defaultExpanded);
		const expanded = isExpanded ?? localExpanded;
		const setExpanded = useCallback(
			(next: boolean) => {
				if (onExpandedChange) onExpandedChange(next);
				else setLocalExpanded(next);
			},
			[onExpandedChange],
		);
		const [isDeleting, setIsDeleting] = useState(false);
		const [isUpdatingAvatar, setIsUpdatingAvatar] = useState(false);
		const [selectedChannelId, setSelectedChannelId] = useState(webhook.channelId);
		const [currentName, setCurrentName] = useState(webhook.name);
		const [localAvatar, setLocalAvatar] = useState<string | null | undefined>(undefined);
		useEffect(() => {
			setLocalAvatar(undefined);
		}, []);
		useEffect(() => {
			if (formVersion == null) return;
			setLocalAvatar(undefined);
			setCurrentName(webhook.name);
			setSelectedChannelId(webhook.channelId);
		}, [formVersion, webhook.name, webhook.channelId]);
		const creator = webhook.creator;
		const creatorDisplayName = creator?.displayName ?? t`Unknown User`;
		const createdAt = useMemo(() => DateUtils.getFormattedShortDate(webhook.createdAt), [webhook.createdAt]);
		const effectiveAvatar: string | null = localAvatar !== undefined ? localAvatar : (webhook.avatar ?? null);
		const avatarUrl = useMemo(() => {
			return AvatarUtils.getWebhookAvatarURL({id: webhook.id, avatar: effectiveAvatar}, false);
		}, [webhook.id, effectiveAvatar]);
		const webhookUrl = useMemo(() => webhook.webhookUrl, [webhook.webhookUrl]);
		const handleCopy = useCallback(async () => {
			try {
				await TextCopyCommands.copy(i18n, webhookUrl);
			} catch (error) {
				logger.error('Failed to copy webhook URL', error);
			}
		}, [i18n, webhookUrl]);
		const handleDelete = useCallback(async () => {
			if (!onDelete) return;
			setIsDeleting(true);
			try {
				await onDelete(webhook);
			} catch (error) {
				logger.error('Failed to delete webhook', error);
				ToastCommands.createToast({type: 'error', children: t`Failed to delete webhook`});
			} finally {
				setIsDeleting(false);
			}
		}, [onDelete, webhook]);
		const handleChannelChange = useCallback(
			(newChannelId: string) => {
				if (newChannelId === webhook.channelId) return;
				setSelectedChannelId(newChannelId);
				onUpdate?.(webhook.id, {channelId: newChannelId});
			},
			[onUpdate, webhook.id, webhook.channelId],
		);
		const handleAvatarUpload = useCallback(async () => {
			const [file] = await openFilePicker({accept: 'image/*'});
			if (!file) return;
			if (file.size > 10 * 1024 * 1024) {
				ToastCommands.createToast({
					type: 'error',
					children: t`Avatar file is too large. Please choose a file smaller than ${IMAGE_MAX_SIZE_LABEL}.`,
				});
				return;
			}
			try {
				setIsUpdatingAvatar(true);
				const base64 = await AvatarUtils.fileToBase64(file);
				setLocalAvatar(base64);
				onUpdate?.(webhook.id, {avatar: base64});
			} catch {
				ToastCommands.createToast({
					type: 'error',
					children: <Trans>That image is invalid. Please try another one.</Trans>,
				});
			} finally {
				setIsUpdatingAvatar(false);
			}
		}, [webhook.id, onUpdate]);
		const handleClearAvatar = useCallback(() => {
			setLocalAvatar(null);
			onUpdate?.(webhook.id, {avatar: null});
		}, [webhook.id, onUpdate]);
		return (
			<div className={styles.container}>
				<FocusRing offset={-2}>
					<button
						type="button"
						className={styles.headerButton}
						onClick={() => setExpanded(!expanded)}
						aria-expanded={expanded}
					>
						<div className={styles.left}>
							<div className={styles.avatarLarge} style={{backgroundImage: `url(${avatarUrl})`}} aria-hidden />
							<div className={styles.textBlock}>
								<div className={styles.titleRow}>
									<span className={styles.name}>{currentName}</span>
									{channelName && <span className={styles.channelTag}>#{channelName}</span>}
								</div>
								<div className={styles.metaRow}>
									<span className={styles.truncate}>
										<Trans>
											Created by {creatorDisplayName} on {createdAt}
										</Trans>
									</span>
									{channelName && <span className={styles.channelTagMobile}>#{channelName}</span>}
								</div>
							</div>
						</div>
						<CaretDownIcon className={clsx(styles.chevron, expanded && styles.chevronExpanded)} weight="bold" />
					</button>
				</FocusRing>
				{expanded && (
					<div className={styles.details}>
						<div className={styles.detailsRow}>
							<div className={styles.avatarColumn}>
								<label htmlFor={`webhook-avatar-${webhook.id}`} className={styles.label}>
									<Trans>Avatar</Trans>
								</label>
								<div className={styles.avatarPreview} style={{backgroundImage: `url(${avatarUrl})`}} aria-hidden />
								<div className={styles.avatarActions}>
									<Button variant="secondary" small={true} onClick={handleAvatarUpload} submitting={isUpdatingAvatar}>
										<Trans>Upload Image</Trans>
									</Button>
									{effectiveAvatar !== null && (
										<Button variant="secondary" small onClick={handleClearAvatar}>
											<Trans>Remove</Trans>
										</Button>
									)}
								</div>
							</div>
							<div className={styles.fields}>
								<div className={styles.fieldsRow}>
									<div className={styles.fieldGrow}>
										<Input
											id={`webhook-name-${webhook.id}`}
											label={t`Name`}
											value={currentName}
											onChange={(event) => {
												const newName = event.target.value;
												setCurrentName(newName);
												onUpdate?.(webhook.id, {name: newName});
											}}
											onBlur={() => {
												if (currentName !== webhook.name) {
													onUpdate?.(webhook.id, {name: currentName});
												}
											}}
											placeholder={t`Webhook name`}
										/>
									</div>
									{availableChannels && availableChannels.length > 0 && (
										<div className={styles.fieldGrow}>
											<Select
												label={t`Channel`}
												value={selectedChannelId}
												options={availableChannels.map((option) => ({
													value: option.id,
													label: option.label,
												}))}
												onChange={handleChannelChange}
											/>
										</div>
									)}
								</div>
								<div className={styles.urlWrapper}>
									<Input
										id={`webhook-url-${webhook.id}`}
										label={t`Webhook URL`}
										value={webhookUrl}
										readOnly
										onFocus={(event) => event.currentTarget.select()}
										className={styles.monoInput}
									/>
								</div>
							</div>
						</div>
						<div className={styles.actions}>
							<Button
								variant="secondary"
								small
								onClick={handleCopy}
								leftIcon={<CopySimpleIcon className={styles.iconSmall} weight="fill" />}
							>
								<Trans>Copy Webhook URL</Trans>
							</Button>
							<Button
								variant="danger-primary"
								onClick={handleDelete}
								submitting={isDeleting}
								leftIcon={<TrashSimpleIcon className={styles.iconSmall} weight="fill" />}
								small={true}
							>
								<Trans>Delete Webhook</Trans>
							</Button>
						</div>
					</div>
				)}
			</div>
		);
	},
);
